import { useEffect, useRef, useState } from "react";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL } from "../lib/landingContent";
import { ProductConsole, PublicBrand } from "./PublicBrand";
import "../styles/sarvam-public.css";

const AUDIENCES = [
  { id: "operators", label: "Operators", placeholder: "Ask the runbook in the words the ticket uses" },
  { id: "developers", label: "Developers", placeholder: "POST /api/v1/chat/query from the apps you already ship" },
  { id: "admins", label: "Admins", placeholder: "Who can query this knowledge base?" },
];
const HOW = [
  ["Ask", "Research with the file", "Ask in the words an operator would use on a call. Retrieval starts from indexed documents, not a general guess."],
  ["Index", "Plan from the library", "PDFs, runbooks, and guides move from pending to ready in the same workspace."],
  ["Cite", "Create an answer you can open", "Every reply returns the documents it used so a reviewer can check the passage."],
  ["Control", "Protect workspace access", "Issue, review, and revoke API keys. Roles stay visible to the people who run the workspace."],
];
const APPS = [
  { name: "Knowledge chat", title: "Ask your documents", text: "Cited answers from the knowledge bases you choose.", page: "chat", action: "Get started", visual: "chat" },
  { name: "Documents", title: "One library", text: "PDFs, Markdown, Word, CSV, and Excel, searchable after indexing.", page: "docs", action: "Learn more", visual: "docs" },
  { name: "Integrations", title: "Your tools", text: "Connector setup and health checks for the systems you already run.", page: "integrations", action: "Learn more", visual: "plug" },
  { name: "Access", title: "Workspace control", text: "Keys, roles, and recorded activity in one place.", page: "apikeys", action: "Learn more", visual: "lock" },
  { name: "Agents", title: "Keys that call the same sources", text: "Issue API keys for the apps that need to query the same knowledge bases.", page: "agents", action: "Learn more", visual: "bot" },
  { name: "API", title: "Same sources, your apps", text: "Query indexed knowledge with a workspace key.", page: "apikeys", action: "Get started", visual: "code" },
];
const PROOF = ["Indexed documents", "Cited answers", "Workspace keys", "Self-hosted"];
const WORKFLOWS = [
  ["Release check", "What has to be true before we ship?", "Release playbook, rollback owner, and customer notice timing.", "chat"],
  ["First response", "Who owns the next step on this incident?", "Runbooks indexed in the workspace, with the passage attached.", "chat"],
  ["New operator", "Where is the guide for this service?", "PDFs, Markdown, and Word docs, searchable after indexing.", "docs"],
  ["Workspace access", "Who can query this knowledge base?", "Roles and API keys issued, reviewed, and revoked in the same workspace.", "apikeys"],
];
const FAQS = [
  ["What can we add?", "PDF, Markdown, text, Word, CSV, or Excel. Indexing status shows in the library. Only indexed documents are available for retrieval."],
  ["Where do answers come from?", "Passages from the knowledge bases you choose. The reply returns the documents it used."],
  ["Does this replace our wiki?", "Keep Confluence, GitHub, and the files you already trust. WeCrew retrieves from what you index."],
  ["Does a cited answer mean the procedure is approved?", "A reviewer still opens the source before acting. The citation shows where the passage came from."],
  ["Can our apps ask the same sources?", "Query the knowledge bases with an API key from the workspace."],
];
const LANGUAGES = ["Python", "JavaScript", "cURL"];
const SNIPPETS = {
  Python: `import os, requests\n\nr = requests.post(\n    os.environ["WECREW_API_URL"] + "/api/v1/chat/query",\n    headers={"Authorization": "Bearer " + os.environ["WECREW_API_KEY"]},\n    json={"query": "What is our release checklist?"},\n)\nprint(r.json()["answer"])`,
  JavaScript: `const r = await fetch(process.env.WECREW_API_URL + "/api/v1/chat/query", {\n  method: "POST",\n  headers: {\n    Authorization: "Bearer " + process.env.WECREW_API_KEY,\n    "Content-Type": "application/json",\n  },\n  body: JSON.stringify({ query: "What is our release checklist?" }),\n});\nconsole.log((await r.json()).answer);`,
  cURL: `curl --fail-with-body \\\n  "$WECREW_API_URL/api/v1/chat/query" \\\n  -H "Authorization: Bearer $WECREW_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"query":"What is our release checklist?"}'`,
};

function scrollToSection(id) {
  if (id === "top") {
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
    return;
  }
  document.getElementById(id)?.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    block: "start",
  });
}

function ContactDialog({ dialogRef }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [prepared, setPrepared] = useState(false);
  return <dialog ref={dialogRef} className="wc-contact" aria-labelledby="contact-title" onClick={event => { if (event.target === event.currentTarget) dialogRef.current.close(); }}>
    <button type="button" className="wc-close" aria-label="Close contact form" onClick={() => dialogRef.current.close()}>×</button>
    <h2 id="contact-title">Contact</h2>
    <p>Tell us what you need.</p>
    <form onSubmit={event => {
      event.preventDefault();
      const body = `Name: ${name}\nEmail: ${email}\n\n${message}`;
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("WeCrew Copilot enquiry")}&body=${encodeURIComponent(body)}`;
      setPrepared(true);
    }}>
      <label>Name<input required value={name} onChange={event => setName(event.target.value)} autoComplete="name"/></label>
      <label>Work email<input required type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="email"/></label>
      <label>Message<textarea required rows={4} value={message} onChange={event => setMessage(event.target.value)}/></label>
      <button className="wc-button wc-button-dark" type="submit">Prepare email</button>
      <p className="wc-fine" role="status">{prepared ? "Draft ready in your mail app." : "Opens a draft for you to review."}</p>
    </form>
    <a className="wc-contact-email" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
  </dialog>;
}

export function LandingPage({ authed, onNavigate, onSignOut }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [audience, setAudience] = useState("operators");
  const [ask, setAsk] = useState("");
  const [language, setLanguage] = useState("Python");
  const [copyStatus, setCopyStatus] = useState("");
  const headerRef = useRef(null);
  const dialogRef = useRef(null);
  const selected = AUDIENCES.find(item => item.id === audience) || AUDIENCES[0];
  useEffect(() => {
    function dismiss(event) {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "keydown" || !headerRef.current?.contains(event.target)) setMobileOpen(false);
    }
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => { document.removeEventListener("pointerdown", dismiss); document.removeEventListener("keydown", dismiss); };
  }, []);
  function enter(page = "dashboard") {
    if (authed) onNavigate("app", page);
    else { sessionStorage.setItem("wecrew_entry_page", page); onNavigate("signup"); }
  }
  function submitAsk(event) {
    event.preventDefault();
    const query = ask.trim();
    if (query) sessionStorage.setItem("wecrew_landing_ask", query);
    enter("chat");
  }
  function go(id) {
    setMobileOpen(false);
    if (id === "contact") { dialogRef.current.showModal(); return; }
    if (id === "developers") { setAudience("developers"); }
    scrollToSection(id);
  }
  return <div className="wc-public">
    <a className="wc-skip" href="#main" onClick={event => { event.preventDefault(); document.getElementById("wc-main").focus(); }}>Skip to content</a>
    <div className="wc-utility">
      <button type="button" onClick={() => onNavigate(authed ? "app" : "signin")}>{authed ? "Workspace" : "Sign in"}</button>
      <button type="button" onClick={() => onNavigate(authed ? "app" : "signup")}>{authed ? "Open" : "Register"}</button>
    </div>
    <header className="wc-header" ref={headerRef}><div className="wc-nav-wrap">
      <div className="wc-brand-lockup">
        <PublicBrand onClick={() => go("top")}/>
        <span className="wc-brand-split" aria-hidden="true"/>
        <span className="wc-product-name">Copilot</span>
      </div>
      <nav className={`wc-nav ${mobileOpen ? "is-open" : ""}`} aria-label="Main navigation">
        <button className="wc-nav-link" onClick={() => go("included")}>Workspace</button>
        <button className="wc-nav-link" onClick={() => go("how-it-works")}>How it works</button>
        <button className="wc-nav-link" onClick={() => go("developers")}>API</button>
        <button className="wc-nav-link" onClick={() => go("contact")}>Support</button>
        <button className="wc-mobile-signin" onClick={() => onNavigate(authed ? "app" : "signin")}>{authed ? "Workspace" : "Sign in"}</button>
      </nav>
      <div className="wc-nav-actions">
        <button className="wc-menu-toggle" aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>{mobileOpen ? "×" : "☰"}</button>
      </div>
    </div></header>
    <p className="wc-trust">Cited answers from the files your shift already trusts</p>
    <main id="wc-main" tabIndex={-1}>
      <section className="wc-hero" aria-labelledby="hero-title">
        <form className="wc-hero-ask" onSubmit={submitAsk}>
          <h1 id="hero-title">Ask</h1>
          <div className="wc-audience" role="tablist" aria-label="Audience">
            {AUDIENCES.map(item => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={audience === item.id}
                className={audience === item.id ? "is-active" : ""}
                onClick={() => setAudience(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <label className="wc-ask-field">
            <span className="wc-sr-only">Ask the workspace</span>
            <input
              value={ask}
              onChange={event => setAsk(event.target.value)}
              placeholder={selected.placeholder}
            />
          </label>
          <p className="wc-hero-stats">{PROOF.join("  ·  ")}</p>
        </form>
        <div className="wc-product-stage">
          <ProductConsole scene={audience} />
        </div>
      </section>
      <section className="wc-section wc-how" id="how-it-works">
        <h2 className="wc-section-title">How it works</h2>
        <div className="wc-how-grid">
          {HOW.map(([kicker, title, text], index) => (
            <article key={title}>
              <span className="wc-how-num">{String(index + 1).padStart(2, "0")}</span>
              <p className="wc-kicker">{kicker}</p>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
        <button className="wc-text-more" onClick={() => enter("chat")}>Learn more about WeCrew Copilot</button>
      </section>
      <section className="wc-highlight" aria-labelledby="highlight-title">
        <div className="wc-highlight-copy">
          <p className="wc-kicker">Chat</p>
          <h2 id="highlight-title">Your operations assistant. Built for the file.</h2>
          <p>Ask from indexed runbooks whenever the ticket lands. The written reply still cites the page it came from.</p>
          <button className="wc-button wc-button-dark" onClick={() => enter("chat")}>Try chat</button>
        </div>
        <div className="wc-highlight-visual" aria-hidden="true">
          <ProductConsole scene={audience} />
        </div>
      </section>
      <section className="wc-section wc-included" id="included">
        <h2 className="wc-section-title">Workspace</h2>
        <div className="wc-app-grid">
          {APPS.map(app => (
            <article key={app.name}>
              <h3>{app.title}</h3>
              <div className={`wc-app-visual is-${app.visual}`} aria-hidden="true"/>
              <p>{app.text}</p>
              <button className="wc-cta-bar" type="button" onClick={() => enter(app.page)}>{app.action}</button>
            </article>
          ))}
        </div>
      </section>
      <section className="wc-section wc-workflows" id="workflows">
        <h2 className="wc-section-title">Workflows</h2>
        <p className="wc-lede">Laya routes a short question to one of these workflows. Jev checks a longer passage. Both return a probability. The written reply still cites your file.</p>
        <div className="wc-workflow-grid">
          {WORKFLOWS.map(([title, question, promise, page]) => (
            <article key={title} className="wc-workflow-card">
              <h3>{title}</h3>
              <p className="wc-workflow-question">{question}</p>
              <p>{promise}</p>
              <button className="wc-text-more" onClick={() => enter(page)}>Learn more</button>
            </article>
          ))}
        </div>
      </section>
      <section className="wc-section wc-developers" id="developers">
        <div className="wc-api-layout">
          <div>
            <p className="wc-kicker">Developers</p>
            <h2>Build with the API</h2>
            <p>Query your knowledge bases from the apps you already ship.</p>
            <button className="wc-button wc-button-dark" onClick={() => enter("apikeys")}>Get an API key</button>
          </div>
          <div className="wc-code-card">
            <div className="wc-code-tabs">{LANGUAGES.map(item => <button key={item} aria-pressed={language === item} onClick={() => { setLanguage(item); setCopyStatus(""); }}>{item}</button>)}<button className="wc-copy" onClick={async () => { try { await navigator.clipboard.writeText(SNIPPETS[language]); setCopyStatus("Copied"); } catch { setCopyStatus("Select the code to copy"); } }}>{copyStatus || "Copy"}</button><span className="wc-sr-only" role="status">{copyStatus}</span></div>
            <pre tabIndex={0} aria-label={`${language} API example`}><code>{SNIPPETS[language]}</code></pre>
          </div>
        </div>
      </section>
      <section className="wc-next" id="get-started">
        <h2>Take the next step with WeCrew Copilot</h2>
        <div className="wc-next-grid">
          <article>
            <h3>Choose your workspace</h3>
            <p>Index the files the shift already trusts, then ask in the words the ticket uses.</p>
            <button className="wc-button wc-button-dark" onClick={() => onNavigate(authed ? "app" : "signup")}>{authed ? "Open workspace" : "Try for free"}</button>
          </article>
          <article>
            <h3>See how answers stay cited</h3>
            <p>Every reply keeps the document so a reviewer can open the passage before acting.</p>
            <button className="wc-button wc-button-outline" onClick={() => enter("chat")}>Ask in chat</button>
          </article>
        </div>
      </section>
      <section className="wc-section wc-faq" id="questions">
        <h2 className="wc-section-title">FAQs</h2>
        <div>
          {FAQS.map(([question, answer], index) => (
            <details key={question}>
              <summary><span className="wc-faq-num">{String(index + 1).padStart(2, "0")}/</span>{question}<span aria-hidden="true">+</span></summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>
    </main>
    <footer className="wc-footer">
      <div className="wc-footer-main">
        <div className="wc-footer-brand">
          <PublicBrand onClick={() => go("top")}/>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE}</a>
        </div>
        <div><h3>Product</h3>{APPS.slice(0, 4).map(item => <button key={item.name} onClick={() => enter(item.page)}>{item.name}</button>)}</div>
        <div><h3>Developers</h3><button onClick={() => go("developers")}>API</button><a href="/docs" target="_blank" rel="noreferrer">Docs</a><button onClick={() => onNavigate("app", "chat")}>Preview</button></div>
        <div><h3>Company</h3><button onClick={() => dialogRef.current.showModal()}>Contact</button><button onClick={() => onNavigate("signin")}>Sign in</button>{authed && <button onClick={onSignOut}>Sign out</button>}</div>
      </div>
      <div className="wc-footer-bottom"><span>© {new Date().getFullYear()} WeCrew</span></div>
    </footer>
    <ContactDialog dialogRef={dialogRef}/>
  </div>;
}
